<template>
    <BaseDialog
        :showDialog="showDialog"
        maxDialogWidth="800"
        maxCardHeight="700"
        :scrollable="true"
        headline="lang_GENERIC_CAPTION_FIX_IT"
        :close="closeDialog"
        :alvqa="alvqa"
    >    
        <template v-slot:dialogText>
            <v-container fluid>

                <v-row class="pb-3">
                    <!-- Source (read-only) -->
                    <v-col cols="6">
                        <v-text-field
                            :value="sourceName"
                            :label="$t('lang_GENERIC_CAPTION_SOURCE')"
                            readonly
                            disabled
                        ></v-text-field>
                    </v-col>

                    <!-- Work Identifier (read-only) -->
                    <v-col cols="6">
                        <v-text-field
                            :value="workIdentifier"
                            :label="$t('lang_PUBLISHING_WORKS_CAPTION_WORK_ID')"
                            readonly
                            disabled
                        ></v-text-field>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="11">
                        <SearchFilter 
                            :context="searchContext" 
                            @keydown.enter="searchForWorks"
                            :alvqa="alvqa + '-textfield-search'"
                        />
                    </v-col>
                    <v-col align-self="end" class="text-right">
                        <BaseIcon 
                            @click="searchForWorks" 
                            icon="fa-search" 
                            tooltipText="lang_GENERIC_CAPTION_SEARCH"
                            :alvqa="alvqa + '-button-search'"
                        />
                    </v-col>
                </v-row>
                <v-divider v-if="works.length > 0"></v-divider>    
                <v-list two-line>
                    <v-list-item-group
                        v-model="selectedWork"
                        @change="selectWork"
                    >
                        <v-list-item
                            v-for="(work, i) in works"
                            :key="work.alvId"
                            :value="i"
                            :alvqa="alvqa + '-result-list-row-' + work.alvId"
                        >
                            <template v-slot:default="{ active }">
                                <v-list-item-content>
                                    <v-list-item-title :alvqa="alvqa + '-result-list-row-' + work.alvId + '-title'">
                                        {{ formatWorkTitle(work) }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle :alvqa="alvqa + '-result-list-row-' + work.alvId + '-subtitle'">
                                        <BaseTooltip
                                            :simpleText="formatComposers(work)"
                                            :alvqa="alvqa + '-result-list-row-' + work.alvId + '-subtitle-text'"
                                        />
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </template>
                        </v-list-item>
                    </v-list-item-group>
                </v-list>
            </v-container>
        </template>
        
        <template v-slot:dialogActions>
            <v-container fluid>
                <v-row>
                    <v-spacer />
                    <v-col class="text-right">
                        <BaseButton 
                            :disabled="selectedWork === null"
                            @click="apply"
                            label="lang_GENERIC_CAPTION_APPLY"
                            :alvqa="alvqa + '-button-apply'"
                        />
                    </v-col>
                </v-row>
            </v-container>        
        </template>
    </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/_base/BaseDialog'
import SearchFilter from '@/components/_filters/SearchFilter'
import BaseIcon from '@/components/_buttons/BaseIcon'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseTooltip from '@/components/_base/BaseTooltip'

export default {
    name: "FixItC3ErrorDialog",
    props: {
        incomeFile: {
            type: Object,
            required: true
        }
    },
    components: {
        BaseDialog,
        SearchFilter,
        BaseIcon,
        BaseButton,
        BaseTooltip,
    },
    data() {
        return {
            searchContext: 'WorksForFixIt',
            showSearchResult: false,
            works: [],
            selectedWork: null,
            alvqa: 'publishing-income-list-dialog-fix-it-c3-error'
        }
    },
    computed: {
        errorItem() {
            return this.$store.getters.getFixItC3ErrorObject
        },
        sourceAlvId() {
            return this.incomeFile && this.incomeFile.sourceAlvId
                ? this.incomeFile.sourceAlvId
                : '';
        },
        sourceName() {
            return this.incomeFile && this.incomeFile.source
                ? this.incomeFile.source
                : '';
        },
        showDialog: {
            get() {
                return this.$store.getters.getShowFixItC3ErrorDialog;
            },
            set(value) {
                this.$store.commit('setShowFixItC3ErrorDialog', value);
            }
        },
        workIdentifier() {
            return this.errorItem && this.errorItem.workIdentifier ? this.errorItem.workIdentifier : '';
        },
        incomeFileAlvId() {
            return this.incomeFile && this.incomeFile.alvId ? this.incomeFile.alvId : '';
        },
    },
    methods: {
        async searchForWorks() {
            this.$store.commit('setIsLoading', true)
            this.works = await this.$store.dispatch('searchWorksForFixIt')
            this.showSearchResult = true
            this.$store.commit('setIsLoading', false)
        },
        formatWorkTitle(work) {
            // Gather the IDs you want to display
            const meta = []
            if (work.gemaNumber) meta.push(work.gemaNumber)
            if (work.iswc) meta.push(work.iswc)
            if (work.alvId) meta.push(work.alvId)

            // If no IDs, just return the title
            if (!meta.length) return work.title

            // Otherwise, "TITLE (id1, id2, id3)"
            return `${work.title} (${meta.join(', ')})`
        },
        formatComposers(work) {
            return Array.isArray(work.composers) ? work.composers.join(' | ') : ''
        },
        selectWork() {
            if (this.selectedWork !== null && this.works[this.selectedWork]) {
                const selectedWorkObj = this.works[this.selectedWork];
                const displayText = this.formatWorkTitle(selectedWorkObj);
                this.$store.commit('setWorksForFixItSearch', displayText);
            }
        },
        apply() {
            if (this.selectedWork !== null) {
                // Retrieve the selected work from the search results array
                const selectedWorkObj = this.works[this.selectedWork];
                // Build the payload for C3 Fix-It
                const payload = {
                    workIdentifier: this.workIdentifier,       // e.g., "399324516"
                    sourceAlvId: this.sourceAlvId,             // e.g., "ALV000211902AG"
                    workAlvId: selectedWorkObj.alvId,          // e.g., "ALV000336121PW"
                };
                // Dispatch the action with the payload
                this.$store.dispatch('fixItC3Error', payload);
                this.closeDialog();
            }
        },
        closeDialog() {
            this.showDialog = false
            this.works = []
            this.selectedWork = null
            this.showSearchResult = false
        }
    },
    mounted() {
        // If the error object has a work title, set it in the store
        if (this.errorItem && this.errorItem.title) {
            this.$store.commit('setWorksForFixItSearch', this.errorItem.title);
        }
    },
}
</script>

